import { useState, useCallback, useEffect } from 'react';
import { Check, FlaskConical, Droplets } from 'lucide-react';
import { useExperiment } from '@/context';
import { Burette } from '@/components/lab';
import ErlenmeyerFlask from '@/components/lab/ErlenmeyerFlask';
import MagneticStirrer from '@/components/lab/MagneticStirrer';
import LabBench from '../LabBench';
import InfoBox from '../InfoBox';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Pipetting Mg Standard',
    content: 'The Mg2+ standard solution has a precisely known concentration, allowing us to determine the exact molarity of our EDTA solution. Use a volumetric pipette for accurate volume delivery. Always read the meniscus at eye level.'
  },
  2: {
    title: 'pH 10 Buffer',
    content: 'The ammonia/ammonium buffer maintains pH 10, which is optimal for EDTA complexation with Ca2+ and Mg2+. At this pH, EDTA exists primarily as Y4- form, which forms stable 1:1 complexes with these metal ions. Below pH 8, complexation is incomplete.'
  },
  3: {
    title: 'EBT Indicator',
    content: 'Eriochrome Black T (EBT) forms a red complex with Mg2+ and Ca2+ ions. As EDTA is added, it preferentially binds metal ions (stronger complex). At the endpoint, all metal is bound to EDTA, and free indicator turns blue. The color change from red to distinct blue signals the endpoint. Add 2-4 drops for standardization.'
  },
  4: {
    title: 'Filling the Burette',
    content: 'Rinse the burette with EDTA solution before filling to prevent dilution. Remove air bubbles from the tip. Fill to or slightly above the zero mark, then drain to zero. Record the initial reading to 0.01 mL precision.'
  },
  5: {
    title: 'Recording Initial Volume',
    content: 'Read the burette at the bottom of the meniscus. Good lighting and a white card behind the burette help with reading. Record to 0.01 mL. This is your Vi (initial volume) for calculating the volume of EDTA used.'
  },
  6: {
    title: 'Titrating to Endpoint',
    content: 'Add EDTA slowly near the endpoint while swirling constantly. The color change occurs rapidly - from red to distinct blue. Stop when the last trace of red disappears and the blue colour persists. One drop can make the difference between an accurate and inaccurate result.'
  },
  7: {
    title: 'Recording Final Volume',
    content: 'After the endpoint, wait 30 seconds to ensure the color is stable (no red returning). Read the final burette volume (Vf). The volume of EDTA used is Vf - Vi. Record immediately to avoid errors.'
  },
  8: {
    title: 'Replicate Titrations',
    content: 'Perform at least 3 trials for statistical reliability. Discard outliers (> 0.3 mL difference from others). Calculate the average volume for concordant trials. Good technique should yield trial volumes within 0.1 mL of each other.'
  }
};

// EBT color transition: Red (#8B2252) -> Distinct Blue (#3B82F6)
const EBT_WINE_RED = '#8B2252';
const EBT_BLUE = '#3B82F6';

function interpolateColor(color1: string, color2: string, factor: number): string {
  const hex = (c: string) => parseInt(c, 16);
  const r1 = hex(color1.slice(1, 3));
  const g1 = hex(color1.slice(3, 5));
  const b1 = hex(color1.slice(5, 7));
  const r2 = hex(color2.slice(1, 3));
  const g2 = hex(color2.slice(3, 5));
  const b2 = hex(color2.slice(5, 7));

  const r = Math.round(r1 + (r2 - r1) * factor);
  const g = Math.round(g1 + (g2 - g1) * factor);
  const b = Math.round(b1 + (b2 - b1) * factor);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

interface TitrationData {
  trial: number;
  vi: number | null;
  vf: number | null;
  volumeEDTA: number | null;
}

export default function Part2Standardization() {
  const { state, updatePartData, completeStep, isStepCompleted } = useExperiment();
  const partState = state?.experimentData?.standardization || {
    currentTrial: 1,
    mgSolutionAdded: false,
    bufferAdded: false,
    indicatorAdded: false,
    flaskColor: '#FFFFFF',
    buretteVolume: 50,
    stopcockOpen: false,
    titrationData: [
      { trial: 1, vi: null, vf: null, volumeEDTA: null },
      { trial: 2, vi: null, vf: null, volumeEDTA: null },
      { trial: 3, vi: null, vf: null, volumeEDTA: null }
    ] as TitrationData[]
  };

  const [stirrerOn, setStirrerOn] = useState(false);
  const [stirrerSpeed, setStirrerSpeed] = useState(5);
  const [titrating, setTitrating] = useState(false);
  const [titrationProgress, setTitrationProgress] = useState(0);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  const currentTrial = partState.currentTrial;
  const currentData = partState.titrationData[currentTrial - 1];

  const updateState = useCallback((updates: Record<string, unknown>) => {
    updatePartData('standardization', { ...partState, ...updates });
  }, [partState, updatePartData]);

  // Handle titration progress
  useEffect(() => {
    if (titrating && stirrerOn) {
      const interval = setInterval(() => {
        setTitrationProgress(prev => {
          if (prev >= 100) {
            setTitrating(false);
            return 100;
          }
          return prev + 2;
        });
      }, 100);
      return () => clearInterval(interval);
    }
  }, [titrating, stirrerOn]);

  // Update flask color based on titration progress
  useEffect(() => {
    if (partState.indicatorAdded) {
      const colorFactor = titrationProgress / 100;
      const newColor = interpolateColor(EBT_WINE_RED, EBT_BLUE, colorFactor);
      updateState({ flaskColor: newColor });
    }
  }, [titrationProgress, partState.indicatorAdded]);

  const handlePipetteMgStandard = useCallback(() => {
    updateState({ mgSolutionAdded: true });
    completeStep('standardization-1');
  }, [updateState, completeStep]);

  const handleAddBuffer = useCallback(() => {
    if (partState.mgSolutionAdded) {
      updateState({ bufferAdded: true });
      completeStep('standardization-2');
    }
  }, [partState.mgSolutionAdded, updateState, completeStep]);

  const handleAddIndicator = useCallback(() => {
    if (partState.bufferAdded) {
      updateState({
        indicatorAdded: true,
        flaskColor: EBT_WINE_RED
      });
      completeStep('standardization-3');
    }
  }, [partState.bufferAdded, updateState, completeStep]);

  const handleFillBurette = useCallback(() => {
    if (partState.indicatorAdded) {
      updateState({ buretteVolume: 50 });
      completeStep('standardization-4');
    }
  }, [partState.indicatorAdded, updateState, completeStep]);

  const handleRecordInitialVolume = useCallback(() => {
    const newData = [...partState.titrationData];
    newData[currentTrial - 1] = { ...newData[currentTrial - 1], vi: 0 };
    updateState({ titrationData: newData });
    completeStep('standardization-5');
  }, [partState.titrationData, currentTrial, updateState, completeStep]);

  const handleStartTitration = useCallback(() => {
    if (currentData?.vi !== null && partState.indicatorAdded) {
      setTitrating(true);
      setStirrerOn(true);
      updateState({ stopcockOpen: true });
    }
  }, [currentData, partState.indicatorAdded, updateState]);

  const handleStopTitration = useCallback(() => {
    setTitrating(false);
    updateState({ stopcockOpen: false });

    // Calculate volume used based on progress
    const volumeUsed = (titrationProgress / 100) * 25 + 10; // 10-35 mL range
    const newData = [...partState.titrationData];
    newData[currentTrial - 1] = {
      ...newData[currentTrial - 1],
      vf: volumeUsed,
      volumeEDTA: volumeUsed - (currentData?.vi || 0)
    };

    updateState({
      titrationData: newData,
      buretteVolume: 50 - volumeUsed
    });

    if (titrationProgress >= 95) {
      completeStep('standardization-6');
    }
  }, [titrationProgress, partState.titrationData, currentTrial, currentData, updateState, completeStep]);

  const handleRecordFinalVolume = useCallback(() => {
    if (currentData?.vf !== null) {
      completeStep('standardization-7');
    }
  }, [currentData, completeStep]);

  const handleNextTrial = useCallback(() => {
    if (currentTrial < 3) {
      updateState({
        currentTrial: currentTrial + 1,
        mgSolutionAdded: false,
        bufferAdded: false,
        indicatorAdded: false,
        flaskColor: '#FFFFFF',
        buretteVolume: 50,
        stopcockOpen: false
      });
      setTitrationProgress(0);
    } else {
      completeStep('standardization-8');
    }
  }, [currentTrial, updateState, completeStep]);

  const getFlaskFillLevel = () => {
    if (partState.bufferAdded) return 45;
    if (partState.mgSolutionAdded) return 35;
    return 0;
  };

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part II: Standardization (Trial {currentTrial}/3)</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Pipette Mg Standard', hint: 'Transfer 25.00 mL of Mg2+ standard' },
            { id: 2, title: 'Add pH 10 Buffer', hint: 'Add 1 mL of ammonia buffer' },
            { id: 3, title: 'Add EBT Indicator', hint: 'Add 2-4 drops of Eriochrome Black T' },
            { id: 4, title: 'Fill Burette', hint: 'Fill burette with EDTA solution to 0 mL' },
            { id: 5, title: 'Record Initial Volume', hint: 'Record the initial burette reading' },
            { id: 6, title: 'Titrate to Endpoint', hint: 'Add EDTA until color changes from red to distinct blue' },
            { id: 7, title: 'Record Final Volume', hint: 'Record the final burette reading' },
            { id: 8, title: 'Repeat Titration', hint: 'Perform trials 2 and 3' }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${isStepCompleted(`standardization-${step.id}`) ? 'completed' : ''} ${
                state?.currentStep === step.id ? 'active' : ''
              }`}
            >
              <div className={`step-number ${isStepCompleted(`standardization-${step.id}`) ? 'done' : ''}`}>
                {isStepCompleted(`standardization-${step.id}`) ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <div className="step-title-row">
                  <span className="step-title">{step.title}</span>
                  {stepInfoContent[step.id] && (
                    <button
                      className="step-info-btn"
                      onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                    >
                      ?
                    </button>
                  )}
                </div>
                {state?.currentStep === step.id && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Titration Data Table */}
        <div className="data-table">
          <h4>Titration Data</h4>
          <table>
            <thead>
              <tr>
                <th>Trial</th>
                <th>Vi (mL)</th>
                <th>Vf (mL)</th>
                <th>Vol EDTA</th>
              </tr>
            </thead>
            <tbody>
              {partState.titrationData.map((data: TitrationData, i: number) => (
                <tr key={i} className={i + 1 === currentTrial ? 'current' : ''}>
                  <td>{data.trial}</td>
                  <td>{data.vi !== null ? data.vi.toFixed(2) : '-'}</td>
                  <td>{data.vf !== null ? data.vf.toFixed(2) : '-'}</td>
                  <td>{data.volumeEDTA !== null ? data.volumeEDTA.toFixed(2) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <LabBench
        experimentName="EDTA Standardization"
        completedCount={state?.completedSteps.filter(s => s.startsWith('standardization-')).length || 0}
        totalSteps={8}
        equipment={['Burette', 'Erlenmeyer flask', 'Pipette', 'Magnetic stirrer']}
        labMode="guided"
        currentStep={state?.currentStep || 1}
      >
        <div className="standardization-workspace">
          <div className="titration-setup">
            {/* Burette */}
            <div className="burette-station">
              <Burette
                volume={partState.buretteVolume}
                maxVolume={50}
                liquidColor="#7DD3FC"
                stopcock={partState.stopcockOpen ? 'open' : 'closed'}
                onStopcock={() => {
                  if (titrating) {
                    handleStopTitration();
                  } else if (isStepCompleted('standardization-5') && !isStepCompleted('standardization-6')) {
                    handleStartTitration();
                  }
                }}
              />
              <span className="equipment-label">EDTA Solution</span>
            </div>

            {/* Flask on Stirrer */}
            <div className="flask-assembly">
              <ErlenmeyerFlask
                size={250}
                fillLevel={getFlaskFillLevel()}
                fillColor={partState.flaskColor}
                hasStirBar={true}
                stirring={stirrerOn}
                label="Titration Flask"
                active={
                  (!isStepCompleted('standardization-1')) ||
                  (!isStepCompleted('standardization-2') && partState.mgSolutionAdded) ||
                  (!isStepCompleted('standardization-3') && partState.bufferAdded)
                }
                onClick={() => {
                  if (!isStepCompleted('standardization-1')) {
                    handlePipetteMgStandard();
                  } else if (!isStepCompleted('standardization-2') && partState.mgSolutionAdded) {
                    handleAddBuffer();
                  } else if (!isStepCompleted('standardization-3') && partState.bufferAdded) {
                    handleAddIndicator();
                  }
                }}
              />
              <MagneticStirrer
                speed={stirrerSpeed}
                isOn={stirrerOn}
                hasFlask={true}
                onClick={() => setStirrerOn(!stirrerOn)}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            {!isStepCompleted('standardization-4') && partState.indicatorAdded && (
              <button className="action-btn primary" onClick={handleFillBurette}>
                <Droplets size={18} />
                Fill Burette
              </button>
            )}

            {!isStepCompleted('standardization-5') && isStepCompleted('standardization-4') && (
              <button className="action-btn primary" onClick={handleRecordInitialVolume}>
                <Check size={18} />
                Record Initial: 0.00 mL
              </button>
            )}

            {isStepCompleted('standardization-5') && !isStepCompleted('standardization-6') && (
              <div className="titration-controls">
                <button
                  className={`action-btn ${titrating ? 'danger' : 'success'}`}
                  onClick={titrating ? handleStopTitration : handleStartTitration}
                >
                  {titrating ? 'Stop Titration' : 'Start Titration'}
                </button>
                <div className="titration-progress">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${titrationProgress}%`,
                        backgroundColor: partState.flaskColor
                      }}
                    />
                  </div>
                  <span>{titrationProgress.toFixed(0)}%</span>
                </div>
              </div>
            )}

            {!isStepCompleted('standardization-7') && isStepCompleted('standardization-6') && currentData?.vf && (
              <button className="action-btn primary" onClick={handleRecordFinalVolume}>
                <Check size={18} />
                Record Final: {currentData.vf.toFixed(2)} mL
              </button>
            )}

            {isStepCompleted('standardization-7') && !isStepCompleted('standardization-8') && (
              <button className="action-btn success" onClick={handleNextTrial}>
                {currentTrial < 3 ? `Start Trial ${currentTrial + 1}` : 'Complete Standardization'}
              </button>
            )}
          </div>

          {/* Color Legend */}
          <div className="color-legend">
            <div className="legend-item">
              <div className="color-swatch" style={{ backgroundColor: EBT_WINE_RED }} />
              <span>Before endpoint (Red)</span>
            </div>
            <div className="legend-item">
              <div className="color-swatch" style={{ backgroundColor: EBT_BLUE }} />
              <span>At endpoint (Distinct blue)</span>
            </div>
          </div>
        </div>
      </LabBench>

      <style>{`
        .standardization-workspace {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
          padding: 20px;
          width: 100%;
        }

        .titration-setup {
          display: flex;
          gap: 60px;
          align-items: flex-end;
        }

        .burette-station {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .flask-assembly {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .equipment-label {
          font-size: 12px;
          color: var(--text-secondary);
        }

        .action-buttons {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .action-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
        }

        .action-btn.primary {
          background: var(--primary);
          color: white;
        }

        .action-btn.success {
          background: var(--secondary);
          color: white;
        }

        .action-btn.danger {
          background: var(--danger);
          color: white;
        }

        .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }

        .titration-controls {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .titration-progress {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 200px;
        }

        .progress-bar {
          flex: 1;
          height: 8px;
          background: var(--bg-dark);
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          transition: width 0.2s, background-color 0.3s;
        }

        .color-legend {
          display: flex;
          gap: 24px;
          padding: 12px 20px;
          background: var(--bg-elevated);
          border-radius: 8px;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: var(--text-secondary);
        }

        .color-swatch {
          width: 20px;
          height: 20px;
          border-radius: 4px;
          border: 1px solid rgba(255,255,255,0.2);
        }

        .data-table {
          margin-top: 20px;
          padding: 12px;
          background: var(--bg-elevated);
          border-radius: 8px;
        }

        .data-table h4 {
          font-size: 12px;
          color: var(--text-muted);
          margin: 0 0 8px 0;
          text-transform: uppercase;
        }

        .data-table table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
        }

        .data-table th, .data-table td {
          padding: 6px 8px;
          text-align: center;
          border-bottom: 1px solid var(--border);
        }

        .data-table th {
          color: var(--text-muted);
          font-weight: 600;
        }

        .data-table tr.current {
          background: rgba(255, 90, 54, 0.1);
        }

        .step-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
      `}</style>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp4"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
